/**
 * Homepage Hero Actions Component
 *
 * Client component for hero section buttons with conversion tracking.
 * Provides responsive button layout with visual hierarchy and icons.
 * Uses centralized navigation configuration for consistency.
 */

'use client';

import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { ANIMATION, HOVER_EFFECTS } from '@/lib/design-tokens';
import { PRIMARY_NAV_LINKS, getAnalyticsSource } from '@/lib/nav-links';
import { ArrowRight } from 'lucide-react';

export function HomepageHeroActions() {
  const handleLinkClick = (href: string) => {
    trackEvent({
      name: 'external_link_clicked',
      properties: {
        url: href,
        source: getAnalyticsSource(href, 'hero'),
      },
    });
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center w-full sm:w-auto">
      {PRIMARY_NAV_LINKS.map((link) => {
        // Determine button variant based on link variant
        const buttonVariant =
          link.variant === 'primary'
            ? 'cta'
            : link.variant === 'secondary'
              ? 'cta-outline'
              : 'secondary';

        return (
          <Button
            key={link.href}
            variant={buttonVariant}
            asChild
            size="sm"
            className={cn(
              'gap-2 sm:gap-3',
              HOVER_EFFECTS.button,
              ANIMATION.transition.base,
              'group relative overflow-hidden',
              // In light the primary is ink, the same ground as the hero's
              // scrim, so the filled CTA takes an inset ring to hold its edge.
              // At 80% the ring measures 3.6:1 or better against the hero
              // just outside it in every cut, at 1440 and 390 wide. At 35% it
              // measured 2.4:1 on a desktop and fell to 1.0:1 on a phone,
              // where the photo's contour lines run behind the button.
              buttonVariant === 'cta' && 'ring-1 ring-inset ring-primary-foreground/80 dark:ring-0'
            )}
          >
            <Link href={link.href} onClick={() => handleLinkClick(link.href)}>
              <link.icon className="h-4 w-4" />
              <span>{link.label}</span>
              <ArrowRight
                className={cn('h-4 w-4 group-hover:translate-x-1', ANIMATION.transition.movement)}
              />
            </Link>
          </Button>
        );
      })}
    </div>
  );
}
